import React, { FC, useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Box, Card, CardContent, Typography, Stepper, Step, StepLabel } from '@mui/material';
import { Alert } from '../../../../shared/lib/common/Alert';
import { BUTTON_TEXT_CLOSE, LABEL_APP_NOTIFICATION } from '../../../../shared/lib/constants';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

import { AppNotificationModalProps } from '../../app/interfaceTypes';
import { RingMyPhoneLogs, RingMyPhoneStatus, useFetchRecentCallLogLazyQuery, useFetchRingMyPhoneLogLazyQuery } from '../../generated';
import LocationModal from './LocationModal';
import CallModal from './CallModal';
import { useAppContext } from '../../app/contexts/AppContext';

const AppNotificationModal: FC<AppNotificationModalProps> = ({ onClose, ringMyPhoneLog }) => {
  const [log, setLog] = useState<RingMyPhoneLogs>();
  const [openLocation, setOpenLocation] = useState<boolean>(false);
  const [openCall, setOpenCall] = useState<boolean>(false);
  const [callLog, setCallLog] = useState<RingMyPhoneLogs>();
  const { currentUser } = useAppContext();
  const { phoneNumber } = currentUser || {};

  const steps = [RingMyPhoneStatus.Initiated, 'DELIVERED'];

  const getActiveStep = (status: RingMyPhoneStatus) => {
    switch (status) {
      case RingMyPhoneStatus.Initiated:
        return 0;
      case RingMyPhoneStatus.Completed:
        return 1;
      case RingMyPhoneStatus.Failed:
        return -1;
      default:
        return 0;
    }
  };

  const getStatusText = (status: RingMyPhoneStatus) => {
    switch (status) {
      case RingMyPhoneStatus.Initiated:
        return '📲 Notification Initiated';
      case RingMyPhoneStatus.Completed:
        return '✅ Notification Completed';
      case RingMyPhoneStatus.Failed:
        return '❌ Notification Failed';
      default:
        return 'Waiting for updates...';
    }
  };

  const getIcon = (status: RingMyPhoneStatus) => {
    switch (status) {
      case RingMyPhoneStatus.Initiated:
        return <CallMadeIcon color="info" sx={{ fontSize: 55 }} />;
      case RingMyPhoneStatus.Completed:
        return <CheckCircleIcon color="success" sx={{ fontSize: 55 }} />;
      case RingMyPhoneStatus.Failed:
        return <ErrorIcon color="error" sx={{ fontSize: 55 }} />;
      default:
        return <PhoneIphoneIcon sx={{ fontSize: 55 }} />;
    }
  };

  const [fetchNotificationLog] = useFetchRingMyPhoneLogLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const { fetchRingMyPhoneLog } = data;
      if (fetchRingMyPhoneLog) {
        setLog(fetchRingMyPhoneLog);
      }
    },
    onError: ({ message }) => {
      Alert.error(message);
    }
  });

  const [fetchCallLog] = useFetchRecentCallLogLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const { fetchRecentCallLog } = data;
      if (fetchRecentCallLog) {
        setCallLog(fetchRecentCallLog);
        setOpenCall(true);
      }
    },
    onError: ({ message }) => {
      Alert.error(message);
    }
  });

  useEffect(() => {
    if (!ringMyPhoneLog?.id) return;

    let pollCount = 0;
    const maxPolls = 10;
    const terminalStatuses = [RingMyPhoneStatus.Completed, RingMyPhoneStatus.Failed];

    const fetchLog = () => {
      fetchNotificationLog({
        variables: { FetchRingMyPhoneLogInput: { id: ringMyPhoneLog.id } }
      }).then((res) => {
        pollCount++;
        const { data } = res;
        const { fetchRingMyPhoneLog } = data || {};
        const { longitude, latitude } = fetchRingMyPhoneLog || {};
        const status = res?.data?.fetchRingMyPhoneLog?.ringMyPhoneStatus;
        if (status && (terminalStatuses.includes(status) || pollCount >= maxPolls)) {
          if (latitude && longitude) {
            setOpenLocation(true);
          }

          if (status === RingMyPhoneStatus.Failed) {
            setTimeout(() => {
              fetchCallLog({
                variables: {
                  fetchRecentCallLogInput: { timeStamp: new Date().toISOString() }
                }
              });
            }, 5000);
          }

          clearInterval(interval);
        }
      });
    };

    // Initial fetch
    fetchLog();

    // Poll every 10 seconds.
    const interval = setInterval(fetchLog, 10000);

    return () => clearInterval(interval);
  }, [ringMyPhoneLog.id]);

  return (
    <>
      <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 99999 }}>
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>{LABEL_APP_NOTIFICATION}</DialogTitle>

        <DialogContent dividers>
          {log?.ringMyPhoneStatus === RingMyPhoneStatus.Failed && (
            <Box
              mt={3}
              p={2}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                borderRadius: 2,
                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                border: '1px solid #ff9800'
              }}
            >
              <ErrorIcon color="warning" sx={{ mr: 1 }} />
              <Typography variant="body2" fontWeight={600}>
                App Notification did not reach the device — WiFi may be off or the app is not installed. We are now initiating the call.
              </Typography>
            </Box>
          )}

          <Box sx={{ maxWidth: 500, mx: 'auto', p: 2 }}>
            <Card sx={{ p: 2, borderRadius: 4, boxShadow: 6 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Box mb={2}>{getIcon(log?.ringMyPhoneStatus || RingMyPhoneStatus.Initiated)}</Box>

                <Typography variant="h6" fontWeight={700}>
                  {getStatusText(log?.ringMyPhoneStatus || RingMyPhoneStatus.Initiated)}
                </Typography>

                <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
                  Real-time notification progress...
                </Typography>

                <Stepper activeStep={getActiveStep(log?.ringMyPhoneStatus || RingMyPhoneStatus.Initiated)} alternativeLabel sx={{ mt: 3 }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, borderTop: (theme) => `1px solid ${theme.palette.divider}`, justifyContent: 'flex-end' }}>
          <Button onClick={onClose} variant="contained" color="primary">
            {BUTTON_TEXT_CLOSE}
          </Button>
        </DialogActions>
      </Dialog>

      {openLocation && log?.latitude && log.longitude && (
        <LocationModal
          open={openLocation}
          onClose={() => {
            onClose();
            setOpenLocation(false);
          }}
          longitude={log.longitude}
          latitude={log.latitude}
          createdAt={log?.createdAt || undefined}
          phone={phoneNumber || ''}
        />
      )}

      {callLog && openCall && (
        <CallModal
          open={openCall}
          onClose={() => {
            onClose();
            setOpenCall(false);
          }}
          ringMyPhoneLog={callLog}
          showApp={true}
        />
      )}
    </>
  );
};

export default AppNotificationModal;
